import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle, List, MapPin, RefreshCw, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useReresolveLocations, useUnresolvedLocations } from '@/hooks/useStorage'
import { usePermission } from '@/lib/permissions'
import { ReplaceUnitDialog } from '@/components/equipment/storage/replace/ReplaceUnitDialog'
import { DeleteStorageUnitDialog } from '@/components/equipment/storage/DeleteStorageUnitDialog'
import { CopyLayoutDialog } from '@/components/inventory/storage/CopyLayoutDialog'
import { StorageList } from '@/components/inventory/storage/list/StorageList'
import { StorageLocationsView } from '@/components/inventory/storage/StorageLocationsView'
import type { StorageListRow } from '@/lib/storageList'

/**
 * Storage overview — which units exist and how full each one is.
 *
 * Renders the same `StorageList` as Equipment → Storage (design note §1): one
 * list, used in both places. Opening a unit navigates to its own page, where
 * the slot map has the width it needs.
 */
export function StorageTab() {
  const { can } = usePermission()
  const navigate = useNavigate()
  const unresolved = useUnresolvedLocations()
  const reresolve = useReresolveLocations()
  const [replacing, setReplacing] = useState<StorageListRow | null>(null)
  const [deleting, setDeleting] = useState<StorageListRow | null>(null)
  const [copying, setCopying] = useState<StorageListRow | null>(null)
  const [unresolvedDismissed, setUnresolvedDismissed] = useState(false)
  // Units (how full each one is) or Locations (where each one stands,
  // z8nrz7fw25). In the URL so a link can open either.
  const [params, setParams] = useSearchParams()
  const view = params.get('view') === 'locations' ? 'locations' : 'units'
  const setView = (v: 'units' | 'locations') =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p)
        if (v === 'units') next.delete('view')
        else next.set('view', v)
        return next
      },
      { replace: true },
    )

  return (
    <div className='space-y-4'>
      <div role='tablist' aria-label='Storage view' className='inline-flex gap-1 rounded-lg bg-gray-100 p-1'>
        {(
          [
            ['units', 'Units', List],
            ['locations', 'Locations', MapPin],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            type='button'
            role='tab'
            aria-selected={view === key}
            onClick={() => setView(key)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              view === key ? 'bg-white font-medium text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
            )}
          >
            <Icon className='h-3.5 w-3.5' /> {label}
          </button>
        ))}
      </div>

      {view === 'locations' ? (
        <StorageLocationsView />
      ) : (
        <StorageList
          unitHref={(row) => `/inventory/storage/${row.nodeId}`}
          onReplace={can('storage.replace') ? (row) => setReplacing(row) : undefined}
          onCopy={can('inventory.configure') ? (row) => setCopying(row) : undefined}
          onDelete={can('inventory.configure') ? (row) => setDeleting(row) : undefined}
        />
      )}

      {replacing && (
        <ReplaceUnitDialog
          open
          onClose={() => setReplacing(null)}
          sourceRootId={replacing.nodeId}
          sourceCode={replacing.code}
          onPlanned={(transfer) => navigate(`/inventory/storage-transfers/${transfer.id}`)}
        />
      )}

      <CopyLayoutDialog
        sourceId={copying?.nodeId ?? null}
        sourceLabel={copying?.name ?? copying?.code ?? 'this unit'}
        onClose={() => setCopying(null)}
        onCopied={(targetId) => navigate(`/inventory/storage/${targetId}`)}
      />

      <DeleteStorageUnitDialog
        unitId={deleting?.nodeId ?? null}
        unitLabel={deleting?.name ?? deleting?.code ?? 'this unit'}
        onClose={() => setDeleting(null)}
      />

      {!unresolvedDismissed && (unresolved.data ?? []).length > 0 && (
        <Card className='border-amber-300'>
          <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-2 pb-2'>
            <CardTitle className='flex items-center gap-2 text-sm font-medium text-amber-800'>
              <AlertTriangle className='h-4 w-4' />
              Unresolved locations ({unresolved.data?.length})
            </CardTitle>
            <div className='flex items-center gap-1'>
              {can('inventory.manage') && (
                <Button
                  size='sm'
                  variant='outline'
                  className='gap-1.5'
                  disabled={reresolve.isPending}
                  onClick={() => reresolve.mutate()}
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', reresolve.isPending && 'animate-spin')} />
                  {reresolve.isPending ? 'Trying again…' : 'Try again'}
                </Button>
              )}
              <Button
                size='icon'
                variant='ghost'
                className='h-7 w-7 text-amber-800 hover:bg-amber-100'
                aria-label='Dismiss'
                onClick={() => setUnresolvedDismissed(true)}
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Plain wording (design note §1): this used to explain the
                migration's own mechanics; a lab user just needs to know what
                to do about it. */}
            <p className='mb-3 text-sm text-muted-foreground'>
              Some older location codes could not be matched to a shelf automatically, so they are
              parked here instead of guessed at. Describe the shelves first under{' '}
              <span className='font-medium'>Equipment → Storage</span>, then try again.
            </p>

            {reresolve.data && (
              <div className='mb-3 space-y-1 rounded-md border bg-muted/30 px-3 py-2 text-sm'>
                <p>
                  <span className='font-medium'>{reresolve.data.placed}</span> placed ·{' '}
                  <span className='font-medium'>{reresolve.data.reviewed}</span> pick-list lines
                  reviewed · <span className='font-medium'>{reresolve.data.stillParked}</span> still
                  need a person
                </p>
                {reresolve.data.reviewed > 0 && (
                  <p className='text-xs text-muted-foreground'>
                    A pick-list line is a snapshot of a printed sheet, so it is marked reviewed
                    rather than re-addressed — the sheet must not change under the picker.
                  </p>
                )}
                {reresolve.data.refused.slice(0, 5).map((r, i) => (
                  <p key={i} className='text-xs text-amber-800'>
                    <code className='rounded bg-muted px-1 py-0.5'>{r.locationCode ?? '(none)'}</code>{' '}
                    {r.reason}
                  </p>
                ))}
              </div>
            )}
            {reresolve.error && (
              <p className='mb-3 text-sm text-destructive'>{String(reresolve.error.message)}</p>
            )}
            <ul className='space-y-1.5 text-sm'>
              {(unresolved.data ?? []).slice(0, 25).map((row) => (
                <li key={row.id} className='flex flex-wrap items-baseline gap-2'>
                  <Badge variant='outline'>{row.entityType}</Badge>
                  <code className='rounded bg-muted px-1.5 py-0.5 text-xs'>
                    {row.originalLocationCode ?? '(none)'}
                  </code>
                  <span className='text-muted-foreground'>{row.reason}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
